/* eslint-disable import/prefer-default-export */

import { getIsoDate } from 'oxygen-lib-ui/helpers/date';

/**
 * Removes duplicate values from an array
 *
 *
 * @param {Array} array the array to dedupe
 * @returns {Array}
 */
function dedupe(array) {
  return [...new Set(array)];
}

/**
 * Extracts product data from post items
 *
 *
 * @param {Array} items an array of post content items
 * @returns {Object}
 */
export function getProductData(items = []) {
  // Posts can contain the following product embeds:
  // - Product
  // - ProductTeaser
  // - ProductTeaserList
  // - ProductEmbed
  // Extract each product type
  const productItems = items.filter(({ type }) => type === 'product');
  const productTeaserItems = items.filter(
    ({ type }) => type === 'productTeaser'
  );
  const productTeaserListItems = items.filter(
    ({ type }) => type === 'productTeaserList'
  );
  const productEmbedItems = items.filter(({ type }) => type === 'productEmbed');
  // Extract "ProductData" and combine into single list
  const allProductData = [
    ...productItems.map(({ data }) => data),
    ...productTeaserItems.map(({ data }) => data.product),
    ...productTeaserListItems.reduce(
      (acc, { data }) => acc.concat(data.products),
      []
    ),
    ...productEmbedItems.map(({ data }) => data.product)
  ];

  if (allProductData.length === 0) {
    // No product data
    return {};
  }

  // List of retailers
  const retailers = allProductData
    .map(
      // Note, gen-products data includes `retailerName`
      // Legacy data uses `retailer`
      ({ retailerName, retailer }) => retailerName || retailer
    )
    .filter(v => !!v);

  // List of product names
  const names = allProductData.map(({ name }) => name).filter(v => !!v);

  // Find min and max price
  const prices = allProductData.map(
    // Note, gen-products data uses `salePrice` and `price`
    // Legacy data uses `currentPrice` and `originalPrice`
    ({ salePrice, price, currentPrice, originalPrice }) => {
      if (salePrice || price) {
        // New product
        return salePrice || price || 0;
      }
      // Legacy product
      // Legacy prices are strings
      const legacyPrice = currentPrice || originalPrice || '0';
      // Remove "$" and convert to cents
      return parseFloat(legacyPrice.replace('$', ''), 10) * 100;
    }
  );
  const minPrice = Math.min(...prices) / 100;
  const maxPrice = Math.max(...prices) / 100;

  // Flattened list of product tags slugs
  const tags = allProductData.reduce(
    (acc, p) =>
      // Extract tag slugs and add to list
      acc.concat((p.tags || []).map(({ slug }) => slug)),
    []
  );
  return {
    retailers: dedupe(retailers),
    tags: dedupe(tags),
    names,
    minPrice,
    maxPrice
  };
}

/**
 * Extracts recipe data from the post's recipe item
 *
 *
 * @param {Array} items an array of post content items
 * @returns {Object}
 */
export function getRecipeData(items = []) {
  const recipe = items.find(i => i.type === 'recipe');
  const toMinutes = seconds => parseInt((seconds || 0) / 60, 10);
  if (!recipe) {
    return {};
  }

  const { cookTime, prepTime } = recipe.data;
  return {
    ...(cookTime
      ? // Convert to minutes
        {
          cookTime: {
            min: toMinutes(cookTime.min),
            max: toMinutes(cookTime.max)
          }
        }
      : undefined),

    ...(prepTime
      ? // Convert to minutes
        {
          prepTime: {
            min: toMinutes(prepTime.min),
            max: toMinutes(prepTime.max)
          }
        }
      : undefined)
  };
}

/**
 * Extracts article level data from the post
 *
 *
 * @param {Object} data the post bundle's data
 * @returns {Object}
 */
export function getArticleData({ isSponsored = false, data = {} }) {
  const formatDate = seconds => {
    if (!seconds) {
      return null;
    }
    return getIsoDate(seconds);
  };
  return {
    authors: data.author ? [data.author.name] : null,
    tags: (data.tags || []).map(t => t.slug),
    categories: (data.categories || []).map(t => t.slug),
    title: data.title,
    datePublished: formatDate(data.publishedAt),
    dateModified: formatDate(data.modifiedAt),
    dateRepromoted: formatDate(data.repromotedAt),
    isSponsored
  };
}

/**
 * introspects a post bundle and returns stats about its content.
 *
 * collected stats:
 *
 * - bundleItemTypes: object whose keys are bundle item types, and whose values
 *     are the occurrence count. NOTE: only includes encountered bundle item
 *     types.
 *
 * @param {Object} postBundle a bundle with layout = post
 * @param {Object[]} postBundle.items[]
 * @param {String} postBundle.items[].type bundle item type
 * @returns {Object}
 */
function getPostStats(postBundle) {
  const bundleItemTypes = (postBundle.items || []).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.type]: acc[curr.type] === undefined ? 1 : acc[curr.type] + 1
    }),
    {}
  );

  return {
    bundleItemTypes,
    products: getProductData(postBundle.items),
    recipe: getRecipeData(postBundle.items),
    article: getArticleData(postBundle)
  };
}

/**
 * introspects the provided content bundles (and their items) and returns
 * an aggregate stats object, with the bundle type as the top-level key.
 *
 * handled bundle types: post
 *
 * @export
 * @param {Object[]} [contentBundles=[]]
 * @param {String} [contentBundles[].layout] bundle layout (post, etc.)
 * @returns {Object} aggregate stats about the content bundles
 */
export function deriveContentStats(contentBundles = []) {
  // NOTE: we're assuming one post per page for now
  const postBundle = contentBundles.find(b => b.layout === 'post');
  const postStats = postBundle ? getPostStats(postBundle) : null;

  return {
    post: postStats
  };
}
